import React, { useState, useEffect } from "react";
import { getBookmarks, removeBookmark } from "../../utils/bookmarksHelper";
import "./BookmarksBar.css";

const BookmarksBar = ({ onToggle }) => {
  const [bookmarks, setBookmarks] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Update bookmarks when localStorage changes
  useEffect(() => {
    const updateBookmarks = () => {
      setBookmarks(getBookmarks());
    };

    // Initial load
    updateBookmarks();

    // Listen for storage changes (when bookmarks are added/removed)
    window.addEventListener("storage", updateBookmarks);

    // Custom event for same-tab updates
    window.addEventListener("bookmarksChanged", updateBookmarks);

    return () => {
      window.removeEventListener("storage", updateBookmarks);
      window.removeEventListener("bookmarksChanged", updateBookmarks);
    };
  }, []);

  // Handle mouse enter/leave to manage scroll behavior
  const [isHoveringBookmarks, setIsHoveringBookmarks] = useState(false);

  // Only prevent body scroll when actively hovering over the bookmarks area
  useEffect(() => {
    if (isExpanded && isHoveringBookmarks) {
      // Prevent body scroll only when hovering over bookmarks
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = ""; // Restore scroll when not expanded/hovering
    }
  }, [isExpanded, isHoveringBookmarks]);

  // Add new useEffect for handling body overflow
  useEffect(() => {
    // Store original overflow style
    const originalOverflow = document.body.style.overflow;

    // Cleanup function that always runs when component unmounts
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []); // Empty dependency array - runs once on mount

  // Don't show the bar if there are no bookmarks
  if (bookmarks.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  const handleRemoveBookmark = (itemId, categoryId, event) => {
    // Stop event propagation to prevent any parent click handlers
    event.stopPropagation();

    const success = removeBookmark(itemId, categoryId);
    if (success) {
      // Update local state immediately for better UX
      setBookmarks((currentBookmarks) =>
        currentBookmarks.filter(
          (item) => !(item.id === itemId && item.categoryId === categoryId)
        )
      );

      // If this was the last bookmark, restore scroll and notify parent
      if (bookmarks.length === 1) {
        document.body.style.overflow = "";
        onToggle?.(false);
        setIsExpanded(false);
      }
    }
  };

  return (
    <div className={`bookmarks-bar ${isExpanded ? "expanded" : ""}`}>
      <div className="bookmarks-bar-header" onClick={toggleExpanded}>
        <div className="bookmarks-info">
          <span className="bookmarks-icon">❤️</span>
          <span className="bookmarks-count">
            {bookmarks.length === 1
              ? "1 позиція"
              : bookmarks.length < 5
              ? `${bookmarks.length} позиції`
              : `${bookmarks.length} позицій`}{" "}
            у закладках
          </span>
        </div>
        <button className="toggle-button" aria-label="Розгорнути закладки">
          {isExpanded ? "▼" : "▲"}
        </button>
      </div>

      {isExpanded && (
        <div
          className="bookmarks-content"
          onMouseEnter={() => setIsHoveringBookmarks(true)}
          onMouseLeave={() => setIsHoveringBookmarks(false)}
        >
          <div className="bookmarks-list">
            {bookmarks.map((item) => (
              <div
                key={`${item.id}-${item.categoryId}`}
                className="bookmark-item"
              >
                <div className="bookmark-image">
                  <img
                    src={item.image}
                    alt={item.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <div className="bookmark-info">
                  <h4 className="bookmark-name">{item.name}</h4>
                  <p className="bookmark-price">{item.price}</p>
                </div>
                <button
                  className="bookmark-remove-btn"
                  onClick={(e) =>
                    handleRemoveBookmark(item.id, item.categoryId, e)
                  }
                  aria-label={`Видалити ${item.name} з закладок`}
                  title="Видалити з закладок"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookmarksBar;
